import { useMemo, useState } from 'react'
import { useAssignSlice, useBillerSlices, useGatewayBeneficiaries, useGateways, useRevokeSlice } from '../../api/queries'
import type { Biller, BillerSlice } from '../../api/types'
import { BffError } from '../../bff/errors'
import { useT } from '../../i18n'
import { Button, DataTable, SelectField, Skeleton, useToast, type Column } from '../../ui'
import screen from '../screen.module.css'

export interface SlicesSectionProps {
  readonly biller: Biller
}

/**
 * The slices a beneficiary organisation holds, managed from its own edit form
 * (V1.1, FR-40). Identities come from payments only: the form offers the
 * identities Core recorded on the chosen gateway that nobody holds yet —
 * nothing is typed, so a typo cannot become a slice that never matches a
 * row. The gateway panel is the other view of the same assignments.
 *
 * The catalogue is fetched here and only here: the form itself never loads
 * ~200 gateways to draw a name field, but a slice needs one to choose from.
 */
export function SlicesSection({ biller }: SlicesSectionProps) {
  const t = useT()
  const { notify } = useToast()
  const held = useBillerSlices(biller.biller_id)
  const catalogue = useGateways()
  const assign = useAssignSlice()
  const revoke = useRevokeSlice()
  const [gatewayId, setGatewayId] = useState('')
  const [identity, setIdentity] = useState('')
  const observed = useGatewayBeneficiaries(gatewayId || null)

  const nameOf = useMemo(() => {
    const byId = new Map((catalogue.data ?? []).map((g) => [g.gateway_id, g.gateway_name]))
    return (id: string) => byId.get(id) ?? id
  }, [catalogue.data])

  const fail = (error: unknown) =>
    notify('danger', error instanceof BffError || error instanceof Error ? error.message : t('error.title'))
  // Recorded on the gateway and held by nobody — the only identities that
  // can be assigned.
  const free = (observed.data?.beneficiaries ?? []).filter((b) => !b.biller_id)
  const busy = assign.isPending || revoke.isPending

  // Choosing the identity IS the assignment: the panel's row selects work
  // the same way, and nothing is left pending for the form's "Save changes"
  // — which saves name and cut-off only — to drop on the floor.
  const submit = (chosen: string) => {
    if (!gatewayId || !chosen) return
    setIdentity(chosen)
    assign.mutate(
      { code: biller.biller_id, gatewayId, beneficiaryCode: chosen },
      {
        onSuccess: () => {
          notify('success', t('billers.slices.assigned', { identity: chosen, gateway: nameOf(gatewayId) }))
          setIdentity('')
          void held.refetch()
        },
        onError: (error) => {
          fail(error)
          setIdentity('')
          // The refusal means the data moved under us (someone else holds it
          // now, or it is no longer recorded): re-read what is really free.
          void observed.refetch()
        },
      },
    )
  }
  const drop = (slice: BillerSlice) =>
    revoke.mutate(
      { code: biller.biller_id, gatewayId: slice.gateway_id, beneficiaryCode: slice.beneficiary_code },
      {
        onSuccess: () => {
          notify('success', t('billers.slices.revoked', { identity: slice.beneficiary_code, gateway: nameOf(slice.gateway_id) }))
          void held.refetch()
        },
        onError: fail,
      },
    )

  const columns: ReadonlyArray<Column<BillerSlice>> = [
    { key: 'gateway', header: t('billers.slices.gateway'), render: (s) => nameOf(s.gateway_id) },
    { key: 'identity', header: t('billers.slices.identity'), render: (s) => <span className={screen.mono}>{s.beneficiary_code}</span> },
    {
      key: 'actions',
      header: '',
      render: (s) => (
        <Button size="sm" variant="secondary" type="button" disabled={busy} onClick={() => drop(s)}>
          {t('billers.slices.revoke')}
        </Button>
      ),
    },
  ]
  // One identity per gateway per organisation: a gateway already held is
  // not offered again (the API refuses it too, naming the identity held).
  const heldOn = new Set((held.data?.slices ?? []).map((s) => s.gateway_id))
  const gateways = (catalogue.data ?? [])
    .filter((g) => !heldOn.has(g.gateway_id))
    .toSorted((a, b) => a.gateway_name.localeCompare(b.gateway_name))

  return (
    <fieldset>
      <legend>{t('billers.slices')}</legend>
      <p className={screen.hint}>{t('billers.slicesHint')}</p>
      {held.isPending ? (
        <Skeleton variant="row" count={2} label={t('common.loading')} />
      ) : held.error ? (
        <p role="alert">{held.error.message}</p>
      ) : held.data.slices.length === 0 ? (
        <p>{t('billers.slices.none')}</p>
      ) : (
        <DataTable
          rows={held.data.slices}
          columns={columns}
          rowKey={(s) => `${s.gateway_id}|${s.beneficiary_code}`}
          sortLabel={(column) => column}
          caption={t('billers.slices')}
        />
      )}

      <p className={screen.hint}>{t('billers.slices.assignHint')}</p>
      <SelectField label={t('billers.slices.gateway')} value={gatewayId} onChange={(e) => { setGatewayId(e.target.value); setIdentity('') }}>
        <option value="">{t('billers.slices.pickGateway')}</option>
        {gateways.map((g) => (
          <option key={g.gateway_id} value={g.gateway_id}>
            {g.gateway_name}
          </option>
        ))}
      </SelectField>
      <SelectField
        label={t('billers.slices.identity')}
        description={t('billers.slices.identityHint')}
        value={identity}
        disabled={busy || !gatewayId || free.length === 0}
        onChange={(e) => submit(e.target.value)}
      >
        <option value="">
          {!gatewayId ? t('billers.slices.pickIdentity') : free.length === 0 && !observed.isPending ? t('billers.slices.noIdentities') : t('billers.slices.pickIdentity')}
        </option>
        {free.map((b) => (
          <option key={b.beneficiary_code} value={b.beneficiary_code}>
            {b.beneficiary_code}{b.beneficiary_name ? ` — ${b.beneficiary_name}` : ''}
          </option>
        ))}
      </SelectField>
    </fieldset>
  )
}
